import { GroupBase, GroupFactory, GroupType } from './group-base';
import { SortBase, SortDir, SortFactory, SortKey, SortType } from './sort-base';
import { Utils } from '../../../../../core/services/utils.service';

export enum LayoutDirection {
	Vertical = 'vertical',
	Sequential = 'sequential'
}

export interface RuleData {
	id: string,
	name: string,
	color: string,
	direction: LayoutDirection,
	group: {
		type: GroupType,
		extendedType: string
	},
	sort: {
		key: SortKey,
		dir: SortDir,
		type: SortType
	},
	percent: number,
	maxFacesQty: number,
	layoutHeight: number,
	fixedVerticalColumn: boolean,
	children: RuleData[]
}

interface Block {
	name: string,
	begin: {
		x: number,
		y: number
	}
}

class BlockWarning {
	public value: boolean;
	private blocks: Array<Block>;

	set BlockWarning(params) {
		if (!params.block) {
			this.value = false;
			this.blocks = new Array<Block>();
			return;
		}

		if (this.blocks.length && this.blocks.some(
			block => block.begin.y === params.block.begin.y && block.begin.x === params.block.begin.x
		)) {
			return;
		}

		this.value = params.value;
		this.blocks.unshift(params.block);
	}

	get blockNames(): string {
		return this.blocks
			.map(block => block.name)
			.join(', ');
	}

	constructor() {
		this.value = false;
		this.blocks = new Array<Block>();
	}
}

export const DEFAULT_RULE: RuleData = {
	id: '',
	name: '',
	color: '',
	direction: LayoutDirection.Vertical,
	group: {
		type: '' as GroupType,
		extendedType: ''
	},
	sort: {
		key: '' as SortKey,
		dir: 'h' as SortDir,
		type: '' as SortType
	},
	percent: 100,
	maxFacesQty: null,
	layoutHeight: null,
	fixedVerticalColumn: false,
	children: []
};

export class RuleLevel {
	id: string;
	name: string = '';
	direction: LayoutDirection;
	group: GroupBase;
	sort: SortBase;
	recommendedPercent: number = 0;
	plannedPercent: number = null;
	finalPercent: number = null;
	calculatedPercent: number = 0;
	actualPercent: number = 0;
	plannedMeters: number = 0;
	actualMeters: number = 0;
	shelfIndexes: Array<string> = [];
	color: string;
	products: Array<number> = [];
	private _maxFacesQty: number = null;
	private _layoutHeight: number = null;
	private _fixedVerticalColumn: boolean = false;
	parent: RuleLevel;
	children: Array<RuleLevel> = [];
	childrenQueue: Array<{ id: string, name: string }> = [];
	blockWarning: BlockWarning = new BlockWarning();
	opened: boolean = false;
	draggable: boolean = false;
	disabled: boolean = false;

	get percent(): number {
		if (this.plannedPercent || this.plannedPercent == 0) {
			return Utils.round(this.plannedPercent, 2);
		}
		return Utils.round(this.recommendedPercent, 2);
	}

	set percent(value: number) {
		const percent = value
			.toString()
			.replace(/[^0-9.]/g, '')
			.replace(/(\..*?)\..*/g, '$1');

		if (this.plannedPercent == 0 && !percent) {
			this.plannedPercent = null;
		} else {
			this.plannedPercent = Utils.round(Number.parseFloat(percent) || 0, 2);
		}
	}

	get maxFacesQty(): number {
		if (this._maxFacesQty != null) {
			return this._maxFacesQty;
		} else {
			return this.parent ? this.parent.maxFacesQty : 0;
		}
	}

	set maxFacesQty(value: number) {
		this._maxFacesQty = value;
	}

	get layoutHeight(): number {
		if (this._layoutHeight != null) {
			return this._layoutHeight;
		} else {
			return this.parent ? this.parent.layoutHeight : 1;
		}
	}

	set layoutHeight(value: number) {
		this._layoutHeight = value;
	}

	get fixedVerticalColumn(): boolean {
		return this._fixedVerticalColumn || this.parent?.fixedVerticalColumn || false;
	}

	set fixedVerticalColumn(value: boolean) {
		this._fixedVerticalColumn = value;
	}

	get isSelectedOptions(): boolean {
		return this._maxFacesQty != null || this._layoutHeight != null;
	}

	get layoutPercent() {
		return this.plannedMeters ? Utils.round(this.finalPercent * this.layoutMeters / this.plannedMeters, 2) : 0;
	}

	get planogramRecommendedPercent(): number {
		if (this.parent) return this.parent.planogramRecommendedPercent * this.recommendedPercent / 100;
		return this.recommendedPercent / 100;
	}

	get layoutMeters() {
		if (this.children.length) {
			return Utils.round(this.children.reduce((total, child) => total + child.layoutMeters, 0));
		} else {
			return Utils.round(this.actualMeters);
		}
	}

	get warningBlock(): BlockWarning {
		return this.blockWarning;
	}

	constructor(data: RuleData = DEFAULT_RULE, parent: RuleLevel = null) {
		this.id = data.id.toString() || '';
		this.name = data.name || '';
		this.direction = data.direction;
		this.group = GroupFactory.getGroup(data.group.type, data.group.extendedType);
		this.sort = SortFactory.getSort(data.sort.key, data.sort.dir, data.sort.type);
		this.plannedPercent = data.percent || null;
		this.color = data.color || Utils.getRandomColor();
		this._maxFacesQty = !data.maxFacesQty && data.maxFacesQty !== 0 ? null : data.maxFacesQty;
		this._layoutHeight = !data.layoutHeight && data.layoutHeight !== 0 ? null : data.layoutHeight;
		this.fixedVerticalColumn = data.fixedVerticalColumn;
		this.parent = parent;
		if (data.children && data.children.length) {
			data.children.forEach(level => {
				this.addChildren(new RuleLevel(level, this));
			});
		}
	}

	addChildren(level: RuleLevel) {
		this.children.push(level);
	}

	removeChildren(idx: number) {
		this.children.splice(idx, 1);
	}

	addProducts(ids: number[]) {
		this.products.length = 0;
		this.products.push(...ids);
	}

	public calculateRecommendedPercent(dict: { string: number }): number {
		return this.products.reduce((total: number, id: number) => total + (dict[id] || 0), 0) * 100;
	}

	changeGroup(group: GroupType, extended: string = '') {
		this.group = GroupFactory.getGroup(group, extended);
		this.children.length = 0;
	}

	changeSort(key: SortKey, dir: SortDir, type: SortType) {
		this.sort = SortFactory.getSort(key, dir, type);
	}

	clearOptions() {
		this._maxFacesQty = null;
		this._layoutHeight = null;
	}

	getRuleData(): RuleData {
		return {
			id: this.id,
			name: this.name,
			color: this.color,
			direction: this.direction,
			group: { type: this.group.type, extendedType: this.group.extendedType },
			sort: { key: this.sort.key, dir: this.sort.dir, type: this.sort.type },
			percent: this.plannedPercent,
			maxFacesQty: this._maxFacesQty,
			layoutHeight: this._layoutHeight,
			fixedVerticalColumn: this.fixedVerticalColumn,
			children: this.children.map(child => child.getRuleData())
		};
	}
}